import React, { useState, useEffect, useRef } from "react";
import "./Header.css";
import "../../components/WebSocketContext.css";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Badge,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";

import ArrowBack from "@mui/icons-material/ArrowBack";
import ArrowForward from "@mui/icons-material/ArrowForward";
import HomeIcon from "@mui/icons-material/Home";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import LinkIcon from "@mui/icons-material/Link";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import AssessmentIcon from "@mui/icons-material/Assessment";
import SettingsIcon from "@mui/icons-material/Settings";
import BuildIcon from "@mui/icons-material/Build";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import DescriptionIcon from "@mui/icons-material/Description";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import NotificationsIcon from "@mui/icons-material/Notifications";
import CloseIcon from "@mui/icons-material/Close";

import { useWebSocket } from "../../components/WebSocketContext";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// --- PAGE INFO ---
const PAGE_INFO = {
  "/": {
    name: "Home",
    icon: <HomeIcon />,
    tagline:
      "Next-Generation AI-Powered Compliance Platform for Global Trade Excellence",
  },
  "/customer-profiling": {
    name: "Customer Profiling",
    icon: <ManageAccountsIcon />,
    tagline:
      "Streamline customer onboarding with intelligent document processing and data extraction",
  },
  "/view-and-upload": {
    name: "Initiate Trade",
    icon: <UploadFileIcon />,
    tagline:
      "Upload, manage, and process trade documents with AI-powered validation and compliance checks",
  },
  "/transaction-chain": {
    name: "Transaction Chain",
    icon: <LinkIcon />,
    tagline:
      "Displays paginated, searchable transaction chains with document status indicators.",
  },
  "/monitoring": {
    name: "Monitoring",
    icon: <MonitorHeartIcon />,
    tagline: "Monitor and manage real-time validation checks for your transactions.",
  },
  "/reporting": {
    name: "Reporting Page",
    icon: <AssessmentIcon />,
    tagline: "Analyze reports for Compliance, Financial, and Audit departments.",
  },
  "/configure-bot": {
    name: "Configure Bot",
    icon: <SettingsIcon />,
    tagline:
      "Manage bot settings, tasks, and activity logs for Compliance, Financial, and Audit departments.",
  },
  "/integrated-tools": {
    name: "Integrated Tools",
    icon: <BuildIcon />,
    tagline: "Manages, configures, and monitors integrated tools with activity logs.",
  },
  "/admin-panel": {
    name: "Admin Panel",
    icon: <AdminPanelSettingsIcon />,
    tagline:
      "Manage users, bots, settings, logs, and notifications across departments.",
  },
  "/transaction-details": {
    name: "Transaction Details",
    icon: <DescriptionIcon />,
    tagline:
      "View and manage detailed transaction information, documents, and validation results.",
  },
  "/runtime-validation": {
    name: "Runtime Validation",
    icon: <FactCheckIcon />,
    tagline:
      "Review and manage real-time validation checks across multiple compliance modules.",
  },
  "/document-manager": {
    name: "Document Manager",
    icon: <FolderOpenIcon />,
    tagline:
      "Manage, edit, and validate transaction documents with real-time compliance insights.",
  },
};

// Store navigation history in sessionStorage to persist across component remounts
const HISTORY_KEY = 'app_navigation_history';
const INDEX_KEY = 'app_navigation_index';

// --- TYPEWRITER ANIMATION ---
const Typewriter = ({ text }) => {
  const [displayText, setDisplayText] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    setDisplayText("");
    setCurrentIndex(0);
  }, [text]);

  useEffect(() => {
    if (!text || currentIndex >= text.split(" ").length) return;

    const words = text.split(" ");
    const timer = setTimeout(() => {
      setDisplayText(words.slice(0, currentIndex + 1).join(" "));
      setCurrentIndex(currentIndex + 1);
    }, 200);

    return () => clearTimeout(timer);
  }, [text, currentIndex]);

  return (
    <Typography variant="body2" className="tagline-text">
      {displayText}
      <span className="typewriter-cursor">|</span>
    </Typography>
  );
};

// --- HEADER ---
const Header = ({ theme = 'dark' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { notifications, markAsRead, clearNotifications, deleteNotification } = useWebSocket();

  // Notification dropdown
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  const unreadCount = notifications.filter((n) => !n.read).length;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDropdown]);

  const handleBellClick = () => setShowDropdown(!showDropdown);

  const handleNotificationClick = async (notification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }

    // Extract transaction_set_id from message
    const transactionIdMatch = notification.message?.match(/transaction set (\d+)/i);
    const transactionId = transactionIdMatch ? transactionIdMatch[1] : null;

    // Navigate to Document Manager if we have transaction ID
    if (transactionId) {
      try {
        const token = localStorage.getItem("authToken");
        if (!token) {
          console.error("No authentication token found.");
          return;
        }

        // Fetch transactions
        const response = await fetch(`${API_BASE_URL}/transactions`, {
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          console.error('Failed to fetch transactions');
          return;
        }

        const transactions = await response.json();
        const transaction = transactions.find(
          t => t.id.toString() === transactionId
        );

        if (!transaction) {
          console.error('Transaction not found');
          return;
        }

        const documents = transaction.documents || [];

        // Fetch validations
        let validations = [];
        let validationId = null;
        try {
          const validationResponse = await fetch(
            `${API_BASE_URL}/documents/validations/${transactionId}`,
            {
              headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`,
              },
            }
          );

          if (validationResponse.ok) {
            const validationData = await validationResponse.json();
            const allValidations = Array.isArray(validationData) ? validationData : [];

            // Format validations
            validations = allValidations.map((v) => ({
              id: v.id,
              transactionId: v.transaction_set_id,
              transactionName: transaction.name,
              checkName: v.validation_check || "Validation Details",
              status: v.status === 'FAILED' ? 'Fail' : v.status === 'PASSED' ? 'Pass' : v.status === 'NOT_APPLICABLE' ? 'Not Applicable' : v.status || 'N/A',
              lastRun: v.last_run || 'N/A',
              evidence: Array.isArray(v.reason_evidence)
                ? v.reason_evidence.join("; ")
                : v.reason_evidence?.discrepancies?.join("; ") || "",
              details: v.status === 'PASSED'
                ? "Validation passed successfully"
                : Array.isArray(v.reason_evidence)
                ? v.reason_evidence[0] || "Validation failed"
                : v.reason_evidence?.discrepancies?.[0] || "Validation failed",
              documentIds: v.document_ids || [],
              progress: 100,
              changeHistory: [],
              highlightFields: [],
              module: 3,
            }));

            // Use validation_id from notification if available, otherwise find first failed
            if (notification.validation_id) {
              validationId = notification.validation_id;
            } else {
              const failedValidation = validations.find(v => v.status === 'Fail');
              if (failedValidation) {
                validationId = failedValidation.id;
              }
            }
          }
        } catch (err) {
          console.error('Error fetching validations:', err);
        }

        // Close dropdown
        setShowDropdown(false);

        // Navigate to document manager
        navigate(`/document-manager?transactionId=${transactionId}`, {
          state: {
            transactionId: transactionId,
            transactionName: transaction.name,
            fromRuntimeValidation: true,
            documents: documents,
            validations: validations,
            validationId: validationId,
          },
        });
      } catch (err) {
        console.error('Error handling notification click:', err);
      }
    }
  };

  const handleClearAll = () => {
    clearNotifications();
    setShowDropdown(false);
  };

  const handleDeleteNotification = (notificationId, event) => {
    event.stopPropagation();
    if (deleteNotification) {
      deleteNotification(notificationId);
    }
  };

  const formatNotificationMessage = (message) => {
    if (!message) return "New alert received";
    
    let cleanedMessage = message.replace(/^Transaction Set \d+:\s*/i, '');
    cleanedMessage = cleanedMessage.replace(/\s+for transaction set \d+$/i, '');
    
    return cleanedMessage;
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "Just now";

    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString();
  };

  // Navigation - Load from sessionStorage on mount
  const [canGoBack, setCanGoBack] = useState(false);
  const [canGoForward, setCanGoForward] = useState(false);
  const [navigationHistory, setNavigationHistory] = useState(() => {
    const saved = sessionStorage.getItem(HISTORY_KEY);
    return saved ? JSON.parse(saved) : [location.pathname];
  });
  const [currentIndex, setCurrentIndex] = useState(() => {
    const saved = sessionStorage.getItem(INDEX_KEY);
    return saved ? parseInt(saved, 10) : 0;
  });
  const [isNavigating, setIsNavigating] = useState(false);
  const [currentPageInfo, setCurrentPageInfo] = useState(PAGE_INFO["/"]);

  // Save to sessionStorage whenever history or index changes
  useEffect(() => {
    sessionStorage.setItem(HISTORY_KEY, JSON.stringify(navigationHistory));
  }, [navigationHistory]);

  useEffect(() => {
    sessionStorage.setItem(INDEX_KEY, currentIndex.toString());
  }, [currentIndex]);

  // --- Page Info ---
  useEffect(() => {
    const pageInfo =
      PAGE_INFO[location.pathname] ||
      PAGE_INFO[
        Object.keys(PAGE_INFO).find((key) => location.pathname.startsWith(key))
      ] ||
      PAGE_INFO["/"];
    setCurrentPageInfo(pageInfo);
  }, [location.pathname]);

  // --- Navigation History ---
  useEffect(() => {
    if (isNavigating) {
      setIsNavigating(false);
      return;
    }
    
    const currentPath = location.pathname;
    const lastPath = navigationHistory[currentIndex];
    
    // Only update if the path actually changed
    if (currentPath !== lastPath) {
      // New navigation - truncate forward history and add new path
      const newHistory = [
        ...navigationHistory.slice(0, currentIndex + 1),
        currentPath,
      ];
      setNavigationHistory(newHistory);
      setCurrentIndex(newHistory.length - 1);
    }
  }, [location.pathname, isNavigating, navigationHistory, currentIndex]);

  useEffect(() => {
    setCanGoBack(currentIndex > 0);
    setCanGoForward(currentIndex < navigationHistory.length - 1);
  }, [navigationHistory, currentIndex]);

  const handleBack = () => {
    if (canGoBack) {
      const prevPath = navigationHistory[currentIndex - 1];
      setIsNavigating(true);
      setCurrentIndex(currentIndex - 1);
      navigate(prevPath);
    }
  };

  const handleForward = () => {
    if (canGoForward) {
      const nextPath = navigationHistory[currentIndex + 1];
      setIsNavigating(true);
      setCurrentIndex(currentIndex + 1);
      navigate(nextPath);
    }
  };

  // --- RENDER ---
  return (
    <AppBar position="static" className={`${theme}-theme`}>
      <Toolbar className="header-toolbar">
          {/* Left side - Navigation */}
          <Box className="nav-buttons">
            <IconButton color="inherit" onClick={handleBack} disabled={!canGoBack}>
              <ArrowBack />
            </IconButton>
            <IconButton
              color="inherit"
              onClick={handleForward}
              disabled={!canGoForward}
            >
              <ArrowForward />
            </IconButton>

            <Box className="page-indicator">
              <span className="page-icon">{currentPageInfo.icon}</span>
              <span className="page-name">{currentPageInfo.name}</span>
            </Box>
            <Box className="header-tagline">
              <Typewriter text={currentPageInfo.tagline} />
            </Box>
          </Box>

          {/* Right side - Notifications + Title */}
          <Box className="right-section">
            <div className="ws-notification-container" ref={dropdownRef}>
              <IconButton
                color="inherit"
                onClick={handleBellClick}
                size="large"
                aria-label={`${unreadCount} unread notifications`}
              >
                {unreadCount > 0 ? (
                  <Badge 
                    badgeContent={unreadCount} 
                    color="error"
                    max={99}
                    overlap="rectangular"
                    anchorOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                  >
                    <NotificationsIcon />
                  </Badge>
                ) : (
                  <NotificationsIcon />
                )}
              </IconButton>

              {showDropdown && (
                <div
                  className="ws-notifications-dropdown"
                  role="dialog"
                  aria-label="Notifications"
                >
                  <div className="ws-dropdown-header">
                    <span className="ws-dropdown-title">
                      Notifications{" "}
                      {notifications.length > 0 && `(${notifications.length})`}
                    </span>
                    {notifications.length > 0 && (
                      <button
                        className="ws-clear-btn"
                        onClick={handleClearAll}
                        aria-label="Clear all notifications"
                      >
                        Clear All
                      </button>
                    )}
                  </div>

                  <div className="ws-notifications-list">
                    {notifications.length === 0 ? (
                      <div className="ws-empty-state">
                        <div className="ws-empty-icon" aria-hidden="true">
                          🔔
                        </div>
                        <div className="ws-empty-text">No notifications yet</div>
                      </div>
                    ) : (
                      notifications.map((notification, index) => (
                        <div
                          key={notification.id || `notification-${index}`}
                          className={`ws-notification-item ${
                            !notification.read ? "unread" : ""
                          }`}
                          onClick={() => handleNotificationClick(notification)}
                          role="button"
                          tabIndex={0}
                          aria-label={`${
                            notification.read ? "Read" : "Unread"
                          } notification: ${notification.type}. ${
                            notification.message
                          }`}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handleNotificationClick(notification);
                            }
                          }}
                          style={{ animationDelay: `${index * 0.05}s` }}
                        >
                          <div className="ws-notification-content">
                            <div className="ws-notification-type">
                              {notification.type || "Alert"}
                            </div>
                            <div className="ws-notification-message">
                              {formatNotificationMessage(notification.message)}
                            </div>
                            <div className="ws-notification-time">
                              {formatTime(notification.timestamp || notification.created_at)}
                            </div>
                          </div>
                          <IconButton
                            className="ws-notification-delete"
                            size="small"
                            onClick={(e) => handleDeleteNotification(notification.id, e)}
                            aria-label="Delete notification"
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            <Typography variant="h2" className="header-title">
              boTeye trade & finance
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>
  );
};

export default Header;